import React, { useEffect, useState } from 'react';
import { API } from '@/utils/API';
import { templateLabel } from '../shared/formUtils';
import { useSubmissionTemplateVersion } from '@/hooks/submission_forms/useSubmissionTemplateVersion';
import { useResetToProdButton } from '@/hooks/submission_forms/useResetToProdButton';
import ResetToProdButton from './ResetToProdButton';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useUserOptional } from '@/providers/UserProvider';

interface Template {
  id: string;
  category: string;
  sub_category: string;
  variant: string;
  form_type: string;
  status: string;
  csv_id: number;
}

const FORM_TYPES = ['create', 'modify', 'request'];
const STATUSES = ['published', 'draft', 'archived'];

const FORM_TYPE_COLORS: Record<string, string> = {
  create: 'bg-blue-100 text-blue-800',
  modify: 'bg-purple-100 text-purple-800',
  request: 'bg-orange-100 text-orange-800',
};

const SubmissionTemplatesIndex: React.FC = () => {
  // Reset All to Prod rewrites every template, so it goes with the manual
  // editing lock (templates are managed in code while this is false).
  const canEdit =
    useUserOptional()?.userData?.can_edit_submission_templates ?? false;
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [formTypeFilter, setFormTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const { getResetAllStatus, triggerResetAll } = useSubmissionTemplateVersion();
  const {
    status: resetAllStatus,
    isInFlight: isResetAllInFlight,
    notice: resetAllNotice,
    error: resetAllError,
    handleTrigger: handleResetAllToProd,
  } = useResetToProdButton({
    getStatus: getResetAllStatus,
    trigger: triggerResetAll,
    confirmMessage:
      "Reset every template to prod's currently published version? This publishes a new local version for each template, copying prod's tree.",
    startedMessage:
      'Reset all to prod started — refresh in a moment to see the updated templates.',
    failedMessage: 'Failed to start reset all to prod',
  });

  useEffect(() => {
    API.authenticated.admin
      .fetch('/submission_templates')
      .then(r => r.json())
      .then((data: Template[]) => setTemplates(data))
      .catch(() => setError('Failed to load templates'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = templates.filter(t => {
    const label = templateLabel(t).toLowerCase();
    const matchSearch =
      !search ||
      label.includes(search.toLowerCase()) ||
      String(t.csv_id).includes(search);
    const matchType = !formTypeFilter || t.form_type === formTypeFilter;
    const matchStatus = !statusFilter || t.status === statusFilter;
    return matchSearch && matchType && matchStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500 font-sans text-sm">
        Loading templates…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-500 font-sans text-sm">
        {error}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50 font-sans text-sm">
      {/* Header */}
      <div className="bg-white border-b px-5 py-3 flex items-center justify-between shrink-0">
        <div>
          <div className="font-semibold text-gray-900 text-base">
            Submission Templates
          </div>
          <div className="text-gray-500 text-xs mt-0.5">
            {templates.length} templates
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <ResetToProdButton
              status={resetAllStatus}
              isInFlight={isResetAllInFlight}
              onClick={handleResetAllToProd}
              label="Reset All to Prod"
              inProgressLabel="Reset all in progress…"
              title="Sync every template to whatever's currently published in prod"
            />
          )}
          <a
            href="/admin"
            className="text-xs text-gray-500 hover:text-gray-800 border border-gray-200 rounded px-3 py-1.5"
          >
            ← Admin
          </a>
          <a
            href="/admin_workflows/questions"
            className="text-xs bg-gray-900 text-white rounded px-3 py-1.5 hover:bg-gray-700"
          >
            Browse Questions
          </a>
        </div>
      </div>

      {(resetAllNotice || resetAllError) && (
        <div
          className={`px-5 py-2 text-xs border-b shrink-0 ${
            resetAllError
              ? 'text-red-700 bg-red-50'
              : 'text-green-700 bg-green-50'
          }`}
        >
          {resetAllError || resetAllNotice}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border-b px-5 py-2.5 flex gap-2 shrink-0">
        <input
          type="text"
          placeholder="Search by name or csv_id…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 border rounded px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
        />
        <select
          value={formTypeFilter}
          onChange={e => setFormTypeFilter(e.target.value)}
          className="border rounded px-2.5 py-1.5 text-xs text-gray-600 bg-white"
        >
          <option value="">All types</option>
          {FORM_TYPES.map(t => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="border rounded px-2.5 py-1.5 text-xs text-gray-600 bg-white"
        >
          <option value="">All statuses</option>
          {STATUSES.map(s => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        {(search || formTypeFilter || statusFilter) && (
          <button
            onClick={() => {
              setSearch('');
              setFormTypeFilter('');
              setStatusFilter('');
            }}
            className="text-xs text-gray-400 hover:text-gray-600 px-1"
          >
            Clear
          </button>
        )}
      </div>

      <div className="text-xs text-gray-400 px-5 py-1.5 bg-white border-b shrink-0">
        {filtered.length} of {templates.length} templates
      </div>

      {/* Table */}
      <div className="flex-1 overflow-y-auto">
        <table className="w-full bg-white text-xs">
          <thead className="sticky top-0 bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-2.5 text-gray-500 font-medium w-16">
                csv_id
              </th>
              <th className="text-left px-4 py-2.5 text-gray-500 font-medium">
                Template
              </th>
              <th className="text-left px-4 py-2.5 text-gray-500 font-medium w-24">
                Type
              </th>
              <th className="text-left px-4 py-2.5 text-gray-500 font-medium w-24">
                Status
              </th>
              <th className="px-4 py-2.5 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(t => (
              <tr key={t.id} className="border-b hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-gray-400">
                  {t.csv_id}
                </td>
                <td className="px-4 py-3 text-gray-800">{templateLabel(t)}</td>
                <td className="px-4 py-3">
                  <span
                    className={`rounded px-1.5 py-0.5 font-mono text-xs ${FORM_TYPE_COLORS[t.form_type] ?? 'bg-gray-100 text-gray-600'}`}
                  >
                    {t.form_type}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <StatusBadge status={t.status} variant="entity" />
                </td>
                <td className="px-4 py-3 text-right">
                  <a
                    href={`/admin_workflows/submission_templates/${t.id}/edit`}
                    className="text-blue-500 hover:text-blue-700 font-medium"
                  >
                    Open
                  </a>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-16 text-center text-gray-400"
                >
                  No templates match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SubmissionTemplatesIndex;
